/**
 * Add documents to an archive by hand.
 *
 * Not everything arrives through a pipeline. A one-off migration, a vendor's
 * export, the twelve documents somebody finds after the system was retired — an
 * archive that can only be filled by orchestration has a gap in it.
 *
 * The grid is the point. Records land described rather than as a pile of
 * filenames, and each file can be described differently in one submission,
 * which is what records work actually looks like. Columns come from the
 * archive's own metadata model, so the same definition that drives ingest
 * mapping and search facets also drives this form — no third place to keep in
 * step.
 */

import {
  Alert,
  Badge,
  Button,
  FileButton,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconInfoCircle, IconTrash, IconUpload } from "@tabler/icons-react";
import { useState } from "react";

import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  uploadArchiveDocuments,
  type ArchiveContainer,
  type ArchiveUploadResult,
} from "@/shared/api/archive";
import { formatBytes } from "./format";

export function ArchiveUploadModal({
  opened,
  onClose,
  container,
  onUploaded,
}: {
  opened: boolean;
  onClose: () => void;
  container: ArchiveContainer;
  onUploaded: () => void;
}) {
  const getToken = useAccessToken();
  const [files, setFiles] = useState<File[]>([]);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ArchiveUploadResult | null>(null);

  // Only facetable fields: the metadata model can carry things nobody types by
  // hand, and a form with every field is a form nobody completes.
  const columns = (container.fields ?? []).filter((f) => f.facetable);

  function addFiles(picked: File[]) {
    if (!picked.length) return;
    setResult(null);
    setFiles((prev) => [...prev, ...picked]);
    setRows((prev) => [
      ...prev,
      ...picked.map((file) => ({
        // A sensible starting title beats an empty box: the filename without
        // its extension is what the person would have typed anyway.
        title: file.name.replace(/\.[^.]+$/, ""),
      })),
    ]);
  }

  function set(index: number, key: string, value: string) {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [key]: value } : row)));
  }

  /** Copy a column down. A batch of like documents shares most of its
   *  metadata, and typing "Mainz" twelve times is how people stop bothering. */
  function fillDown(key: string) {
    const first = rows[0]?.[key] ?? "";
    if (!first) return;
    setRows((prev) => prev.map((row) => ({ ...row, [key]: first })));
  }

  function remove(index: number) {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setRows((prev) => prev.filter((_, i) => i !== index));
  }

  async function submit() {
    setBusy(true);
    try {
      const outcome = await uploadArchiveDocuments(
        await getToken(),
        container.container_id,
        files,
        rows,
      );
      setResult(outcome);
      notifications.show({
        title: `${outcome.archived} archived`,
        message:
          outcome.failed || outcome.skipped
            ? `${outcome.skipped} already present, ${outcome.failed} failed — see the list.`
            : "Checksummed, indexed, retention applied, and recorded in the audit trail.",
        color: outcome.failed ? "orange" : "teal",
      });
      if (outcome.archived) {
        setFiles([]);
        setRows([]);
        onUploaded();
      }
    } catch (err) {
      notifications.show({
        title: "Upload failed",
        message: err instanceof Error ? err.message : String(err),
        color: "red",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title={`Add documents to ${container.name}`} size="xl">
      <Stack gap="md">
        <Alert variant="light" color="navy" icon={<IconInfoCircle size={18} />}>
          Uploaded documents take the same path as anything a pipeline archives — checksummed, text
          extracted and indexed, this archive&apos;s retention applied, written to storage and
          recorded in the audit trail. Retention is <strong>{container.retention_class}</strong>{" "}
          from each record&apos;s effective date.
        </Alert>

        <Group>
          <FileButton multiple accept="application/pdf" onChange={addFiles}>
            {(props) => (
              <Button {...props} leftSection={<IconUpload size={16} />} variant="light">
                Choose files
              </Button>
            )}
          </FileButton>
          {files.length > 0 && (
            <Text size="sm" c="dimmed">
              {files.length} file{files.length === 1 ? "" : "s"} ·{" "}
              {formatBytes(files.reduce((sum, f) => sum + f.size, 0))}
            </Text>
          )}
        </Group>

        {files.length > 0 && (
          <ScrollArea.Autosize mah={360}>
            <Table verticalSpacing="xs" fz="xs" striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={190}>File</Table.Th>
                  <Table.Th w={190}>Title</Table.Th>
                  {columns.map((field) => (
                    <Table.Th key={field.key} w={150}>
                      <Group gap={4} wrap="nowrap">
                        <span>{field.label}</span>
                        {files.length > 1 && (
                          <Button
                            size="compact-xs"
                            variant="subtle"
                            px={4}
                            onClick={() => fillDown(field.key)}
                            title="Copy the first row down"
                          >
                            ↓
                          </Button>
                        )}
                      </Group>
                    </Table.Th>
                  ))}
                  <Table.Th w={40} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {files.map((file, index) => (
                  <Table.Tr key={`${file.name}-${index}`}>
                    <Table.Td>
                      <Text size="xs" lineClamp={1}>
                        {file.name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {formatBytes(file.size)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <TextInput
                        size="xs"
                        value={rows[index]?.title ?? ""}
                        onChange={(e) => set(index, "title", e.currentTarget.value)}
                      />
                    </Table.Td>
                    {columns.map((field) => (
                      <Table.Td key={field.key}>
                        <TextInput
                          size="xs"
                          placeholder={field.required ? "required" : ""}
                          value={rows[index]?.[field.key] ?? ""}
                          onChange={(e) => set(index, field.key, e.currentTarget.value)}
                        />
                      </Table.Td>
                    ))}
                    <Table.Td>
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        color="red"
                        onClick={() => remove(index)}
                        aria-label={`Remove ${file.name}`}
                      >
                        <IconTrash size={13} />
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>
        )}

        {result && (
          <Alert
            variant="light"
            color={result.failed ? "orange" : "teal"}
            title={`${result.archived} archived, ${result.skipped} skipped, ${result.failed} failed`}
          >
            <Stack gap={4}>
              {result.items.slice(0, 8).map((item, i) => {
                const row = item as Record<string, unknown>;
                return (
                  <Group key={i} gap={8}>
                    <Badge
                      size="xs"
                      variant="light"
                      color={
                        row.status === "archived"
                          ? "teal"
                          : row.status === "skipped"
                            ? "gray"
                            : "red"
                      }
                      tt="none"
                    >
                      {String(row.status)}
                    </Badge>
                    <Text size="xs">{String(row.filename)}</Text>
                    {row.reason ? (
                      <Text size="xs" c="dimmed">
                        {String(row.reason)}
                      </Text>
                    ) : null}
                  </Group>
                );
              })}
            </Stack>
          </Alert>
        )}

        <Group justify="space-between">
          <Text size="xs" c="dimmed">
            A document already archived under the same source id is skipped, not archived twice.
          </Text>
          <Button
            leftSection={<IconUpload size={16} />}
            disabled={!files.length}
            loading={busy}
            onClick={() => void submit()}
          >
            Archive {files.length || ""} document{files.length === 1 ? "" : "s"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
